import { ChangeDetectorRef, Component, HostListener, NgZone, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ToastrService } from 'ngx-toastr';
import { FullUser } from '@reflact/reflactportal';
import { TileService } from 'src/app/shared/services/tile.service';
import { UserService } from 'src/app/shared/services/user.service';
import { MandantService } from 'src/app/shared/services/mandant.service';
import { Tile, TileLayoutItem, TileSize, TileVisibilityDebugEntry, TileVisibilityReason } from 'src/app/shared/types/tile.types';
import { MAX_SEPARATOR_TITLE_LENGTH, isInSnapZone, newSeparatorId, orderTilesByLayout, resolveDefaultLayout, separatorTile, toTileLayout } from 'src/app/shared/utils/tile-layout.util';
import { WebTileEditorComponent } from 'src/app/shared/components/web-tile-editor/web-tile-editor.component';
import { ActivityService } from 'src/app/shared/services/activity.service';
import { HomeWidgetDialogComponent } from 'src/app/shared/components/home-widgets/home-widget-dialog.component';

/** `FullUser` kennt `tiles` (individuelle Kachel-Grants im aktuellen Mandanten) noch nicht — das
 * Feld lebt rein im Portal (siehe docs/mandanten-rollen-rechte.md, zweigleisiges Modell). */
type MandantUserWithTiles = FullUser & { tiles: string[] };

/** Übersetzt die Debug-Reason-Codes aus `/api/tile/listMyDebug` (siehe tile-logto.service.ts) in
 * lesbaren Text fürs versteckte Debug-Overlay (Strg+Alt+D). */
const VISIBILITY_REASON_LABELS: Record<TileVisibilityReason, string> = {
  assignedToOrg: $localize`:@@tileDebugReasonAssignedToOrg:Gehört zur Organisation`,
  individualGrant: $localize`:@@tileDebugReasonIndividualGrant:Wurde individuell zugewiesen`,
};

const TILE_SIZE_LABELS: Record<TileSize, string> = {
  '1x1': $localize`:@@tileSizeSmall:Klein (1×1)`,
  '2x1': $localize`:@@tileSizeWide:Breit (2×1)`,
  '1x2': $localize`:@@tileSizeTall:Hoch (1×2)`,
  '2x2': $localize`:@@tileSizeLarge:Groß (2×2)`,
};

const EXTRA_TILES_TITLE = $localize`:@@tileLayoutExtraTiles:Zusätzliche Kacheln – nur für dich`;
const NEW_SEPARATOR_TITLE = $localize`:@@tileLayoutNewSeparator:Neuer Abschnitt`;

/** Dauer, mit der Nachbarkacheln beim Ziehen an ihren neuen Platz gleiten. */
const TILE_SHIFT_MS = 260;

@Component({
  selector: 'app-tools-and-trainings',
  templateUrl: './tools-and-trainings.component.html',
  styleUrls: ['./tools-and-trainings.component.scss'],
  host: { '[class.tile-page]': '!managementView' },
  standalone: false
})
export class ToolsAndTrainingsComponent implements OnInit, OnDestroy {
  private readonly activity = inject(ActivityService);

  public recordTileOpen(event: MouseEvent, tile: Tile): void {
    if (!tile.personal && (event.type === 'click' || event.button === 1)) {
      this.activity.record('tile_open', tile._id);
    }
  }

  constructor(public userService: UserService, public tileServide: TileService, private mandantService: MandantService, private toastrService: ToastrService, private cdr: ChangeDetectorRef) {
  }



  public tiles: Tile[] = [];
  /** Private Weblink-Kacheln des Nutzers (mandantenunabhängig, nur für ihn sichtbar). */
  public personalTiles: Tile[] = [];
  public tileSearch = '';

  /** Kacheln, die im Raster angeordnet werden: beim Bearbeiten des Standardlayouts die für alle
   * sichtbaren Kacheln des Mandanten, sonst die eigenen. */
  private get layoutTiles(): Tile[] {
    if (this.editingDefaultLayout) return this.defaultLayoutTiles;
    return this.personalTiles.length ? [...this.tiles, ...this.personalTiles] : this.tiles;
  }

  /** Kacheln und Separatoren in Layout-Reihenfolge. */
  private get allTiles(): Tile[] {
    const separators = this.separatorTiles;
    return orderTilesByLayout(separators.length ? [...this.layoutTiles, ...separators] : this.layoutTiles, this.tileLayout);
  }

  private separatorCache?: { layout: TileLayoutItem[]; tiles: Tile[] };

  /** Separatoren aus dem Layout, je Layout-Stand nur einmal erzeugt (stabile Objekte fürs Raster). */
  private get separatorTiles(): Tile[] {
    if (this.separatorCache?.layout !== this.tileLayout) {
      this.separatorCache = { layout: this.tileLayout, tiles: this.tileLayout.filter(item => item.separator).map(separatorTile) };
    }
    return this.separatorCache.tiles;
  }

  /** Aktuell angezeigtes Layout (Reihenfolge, Größe, ausgeblendet, Separatoren) — leer heißt
   * eingebaute Reihenfolge, alles 1×1. Ohne eigenes Layout aus dem Standardlayout zusammengesetzt. */
  public tileLayout: TileLayoutItem[] = [];
  /** Hat der Nutzer in diesem Mandanten ein eigenes Layout gespeichert? */
  public hasPersonalLayout = false;
  /** Standardlayout des Mandanten (#116), Grundlage für Nutzer ohne eigenes Layout. */
  public defaultLayoutItems: TileLayoutItem[] = [];
  /** Mandanten- oder Super-Admin bearbeitet gerade das Standardlayout statt der eigenen Startseite. */
  public editingDefaultLayout = false;
  public defaultLayoutTiles: Tile[] = [];
  public resetConfirmVisible = false;
  public readonly maxSeparatorTitleLength = MAX_SEPARATOR_TITLE_LENGTH;
  public tileSizes: Record<string, TileSize> = {};
  /** Von der Startseite entfernte Kacheln — die Suche findet sie weiterhin. */
  public hiddenTileIds = new Set<string>();
  public readonly tileSizeOptions = (Object.keys(TILE_SIZE_LABELS) as TileSize[]).map(size => ({ size, label: TILE_SIZE_LABELS[size] }));
  /** "Startseite anpassen" (wie der Wackelmodus bei iOS/iCloud): Kacheln lassen sich verschieben,
   * in der Größe ändern und entfernen, Links sind deaktiviert. */
  public arrangeMode = false;
  public addTilesDialogVisible = false;
  /** Ansage für Screenreader nach dem Verschieben oder Ändern der Größe. */
  public layoutStatus = '';
  /** Speichervorgänge laufen nacheinander, damit ein älterer Stand nie einen neueren überschreibt. */
  private layoutSave: Promise<unknown> = Promise.resolve();
  private longPressTimer?: ReturnType<typeof setTimeout>;
  private longPressStart?: { x: number; y: number };
  /** Nach langem Drücken folgt beim Loslassen noch ein Klick — der darf die Kachel nicht öffnen. */
  private suppressNextClick = false;
  /** Aktuelle Zeigerposition beim Ziehen (siehe onDragPointerMove), für tileSortPredicate. */
  private dragPointer?: { x: number; y: number };
  /** Kachel, mit der zuletzt getauscht wurde: erst wieder tauschbar, wenn der Zeiger sie verlassen hat. */
  private lastSwapTarget?: HTMLElement;
  /** Kandidat aus tileSortPredicate, wird in animateTileShift zu lastSwapTarget. */
  private pendingSwapTarget?: HTMLElement;
  /** Solange die Nachbarn noch gleiten, wird nicht erneut umsortiert. */
  private sortLockedUntil = 0;
  private readonly ngZone = inject(NgZone);
  private tilePositions = new Map<HTMLElement, { left: number; top: number }>();

  public tileSize(tile: Tile): TileSize {
    return this.tileSizes[tile._id] ?? '1x1';
  }

  public tileSizeLabel(tile: Tile): string {
    return TILE_SIZE_LABELS[this.tileSize(tile)];
  }

  public get hiddenTiles(): Tile[] {
    return this.hiddenTileIds.size ? this.allTiles.filter(tile => this.hiddenTileIds.has(tile._id)) : [];
  }

  public startArranging(): void {
    this.tileSearch = '';
    this.arrangeMode = true;
  }

  public stopArranging(): void {
    this.arrangeMode = false;
    this.layoutStatus = '';
    if (this.editingDefaultLayout) void this.stopEditingDefaultLayout();
  }

  @HostListener('document:keydown.escape')
  public onEscape(): void {
    const dialogOpen = this.addTilesDialogVisible || this.resetConfirmVisible || this.widgetDialog?.visible || this.personalTileEditor?.visible;
    if (this.arrangeMode && !dialogOpen) this.stopArranging();
  }

  /** Wechselt ins Bearbeiten des Standardlayouts: Es stehen nur Kacheln zur Wahl, die alle sehen. */
  public async startEditingDefaultLayout(): Promise<void> {
    if (!this.canManageActiveTiles) return;
    await this.layoutSave;
    try {
      const { items, tiles } = await this.tileServide.getDefaultTileLayout();
      this.defaultLayoutTiles = tiles;
      this.editingDefaultLayout = true;
      this.setLayout(items);
      this.startArranging();
      this.layoutStatus = $localize`:@@tileDefaultLayoutEditing:Du bearbeitest jetzt das Standardlayout`;
    } catch (error) {
      console.error('Failed to load default tile layout', error);
      this.toastrService.error($localize`:@@tileDefaultLayoutLoadError:Das Standardlayout konnte nicht geladen werden.`);
    }
    this.cdr.detectChanges();
  }

  /** Zurück zur eigenen Startseite, die danach das gespeicherte Standardlayout berücksichtigt. */
  private async stopEditingDefaultLayout(): Promise<void> {
    await this.layoutSave;
    this.editingDefaultLayout = false;
    this.defaultLayoutTiles = [];
    // Den Einstieg aus der Verwaltung nicht beim nächsten Neuladen wiederholen.
    if (this.route.snapshot.queryParamMap?.has('standardlayout')) {
      void this.router?.navigate([], { relativeTo: this.route, queryParams: { standardlayout: null }, queryParamsHandling: 'merge', replaceUrl: true });
    }
    await this.loadTileLayout();
    this.cdr.detectChanges();
  }

  public get canResetLayout(): boolean {
    return this.editingDefaultLayout ? this.tileLayout.length > 0 : this.hasPersonalLayout;
  }

  /** „Standard wiederherstellen“ fragt nach, weil die eigene Anordnung verloren geht. */
  public requestResetLayout(): void {
    this.resetConfirmVisible = true;
  }

  public async confirmResetLayout(): Promise<void> {
    this.resetConfirmVisible = false;
    await this.resetLayout();
  }

  /** Langes Drücken auf eine Kachel startet das Anpassen, wie auf dem iPhone-Homescreen. */
  public onTilePointerDown(event: PointerEvent): void {
    if (this.arrangeMode || this.managementView || event.button !== 0) return;
    // In einer Notiz markiert langes Drücken Text, statt das Anpassen zu starten.
    if ((event.target as HTMLElement | null)?.closest?.('textarea, input')) return;
    this.longPressStart = { x: event.clientX, y: event.clientY };
    this.longPressTimer = setTimeout(() => {
      this.longPressTimer = undefined;
      this.suppressNextClick = true;
      this.startArranging();
      this.cdr.detectChanges();
    }, 500);
  }

  public onTilePointerMove(event: PointerEvent): void {
    if (this.longPressStart && Math.hypot(event.clientX - this.longPressStart.x, event.clientY - this.longPressStart.y) > 8) {
      this.cancelLongPress();
    }
  }

  public cancelLongPress(): void {
    clearTimeout(this.longPressTimer);
    this.longPressTimer = undefined;
    this.longPressStart = undefined;
  }

  public onTileClick(event: MouseEvent, tile: Tile): void {
    if (this.arrangeMode || this.suppressNextClick) {
      event.preventDefault();
      this.suppressNextClick = false;
      return;
    }
    this.recordTileOpen(event, tile);
  }

  public onTileDrop(event: CdkDragDrop<Tile[]>): void {
    this.endTileDrag();
    if (event.previousIndex === event.currentIndex) return;
    const ordered = [...this.filteredTiles];
    moveItemInArray(ordered, event.previousIndex, event.currentIndex);
    this.applyLayout(ordered, ordered[event.currentIndex]);
  }

  public startTileDrag(grid: HTMLElement): void {
    this.dragPointer = undefined;
    this.lastSwapTarget = this.pendingSwapTarget = undefined;
    this.sortLockedUntil = 0;
    this.captureTilePositions(grid);
    this.ngZone.runOutsideAngular(() => window.addEventListener('pointermove', this.onDragPointerMove));
  }

  public endTileDrag(): void {
    window.removeEventListener('pointermove', this.onDragPointerMove);
    this.dragPointer = this.lastSwapTarget = this.pendingSwapTarget = undefined;
    this.tilePositions.clear();
  }

  /** pointermove kommt vor den mousemove/touchmove-Handlern der CDK, so ist die Position im Prädikat
   * aktuell. Außerhalb der Zone registriert, damit nicht jede Mausbewegung Change Detection auslöst. */
  private readonly onDragPointerMove = (event: PointerEvent): void => {
    this.dragPointer = { x: event.clientX, y: event.clientY };
    if (this.lastSwapTarget && this.tileWrapperAt(this.dragPointer) !== this.lastSwapTarget) this.lastSwapTarget = undefined;
  };

  /** Merkt sich die Positionen im Raster ohne Transformationen, als Ausgangspunkt für animateTileShift. */
  public captureTilePositions(grid: HTMLElement): void {
    this.tilePositions = new Map(this.gridChildren(grid).map(el => [el, { left: el.offsetLeft, top: el.offsetTop }]));
  }

  /** Beim Ziehen sortiert die CDK im gemischten Modus das DOM direkt um, die Nachbarn springen dann.
   * Stattdessen gleiten sie von der alten an die neue Position (FLIP). */
  public animateTileShift(grid: HTMLElement): void {
    this.lastSwapTarget = this.pendingSwapTarget;
    this.pendingSwapTarget = undefined;
    this.sortLockedUntil = performance.now() + TILE_SHIFT_MS;
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    for (const el of this.gridChildren(grid)) {
      const before = this.tilePositions.get(el);
      const dx = before ? before.left - el.offsetLeft : 0;
      const dy = before ? before.top - el.offsetTop : 0;
      if (!reduceMotion && (dx || dy)) {
        el.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'none' }], { duration: TILE_SHIFT_MS, easing: 'cubic-bezier(0.2, 0.9, 0.3, 1.1)' });
      }
    }
    this.captureTilePositions(grid);
  }

  private gridChildren(grid: HTMLElement): HTMLElement[] {
    return Array.from(grid.children).filter((el): el is HTMLElement => el instanceof HTMLElement);
  }

  /** Wann die gezogene Kachel umsortiert wird. Die CDK tauscht, sobald der Zeiger irgendeine andere
   * Kachel berührt; bei unterschiedlich großen Kacheln und während die Nachbarn noch gleiten, liegt der
   * Zeiger danach oft wieder auf der getauschten Kachel und es wird hin- und hergetauscht. Daher:
   * - nicht, solange die letzte Verschiebung noch animiert,
   * - nicht erneut mit derselben Kachel, bevor der Zeiger sie verlassen hat,
   * - erst, wenn der Zeiger in der mittleren Hälfte der Zielkachel liegt. */
  public readonly tileSortPredicate = (): boolean => {
    const pointer = this.dragPointer;
    if (!pointer) return true;
    if (performance.now() < this.sortLockedUntil) return false;
    const target = this.tileWrapperAt(pointer);
    if (!target) return true;
    if (target === this.lastSwapTarget || !isInSnapZone(target.getBoundingClientRect(), pointer.x, pointer.y)) return false;
    this.pendingSwapTarget = target;
    return true;
  };

  private tileWrapperAt(pointer: { x: number; y: number }): HTMLElement | undefined {
    return document.elementFromPoint(pointer.x, pointer.y)?.closest<HTMLElement>('.tile-wrapper') ?? undefined;
  }

  public setTileSize(tile: Tile, size: TileSize): void {
    this.tileSizes = { ...this.tileSizes, [tile._id]: size };
    this.applyLayout(this.filteredTiles, tile);
  }

  /** Neuer Separator oben im Raster; das Titelfeld bekommt gleich den Fokus. */
  public addSeparator(): void {
    if (!this.arrangeMode) this.startArranging();
    const separator = separatorTile({ tileId: newSeparatorId(), title: NEW_SEPARATOR_TITLE });
    this.saveLayout([separator, ...this.filteredTiles]);
    this.layoutStatus = $localize`:@@tileSeparatorAdded:Separator hinzugefügt`;
    this.cdr.detectChanges();
    setTimeout(() => {
      const input = document.getElementById(this.separatorInputId(separator)) as HTMLInputElement | null;
      input?.focus();
      input?.select();
    });
  }

  public separatorInputId(separator: Tile): string {
    return `tile-separator-${separator._id.replace(/[^A-Za-z0-9_-]/g, '')}`;
  }

  public renameSeparator(separator: Tile, title: string): void {
    const cleaned = title.replace(/\s+/g, ' ').trim().slice(0, MAX_SEPARATOR_TITLE_LENGTH) || NEW_SEPARATOR_TITLE;
    if (cleaned === separator.name) return;
    this.saveLayout(this.filteredTiles.map(tile => tile._id === separator._id ? { ...tile, name: cleaned, text: cleaned } : tile));
    this.layoutStatus = $localize`:@@tileSeparatorRenamed:Separator umbenannt in ${cleaned}:title:`;
  }

  public removeSeparator(separator: Tile): void {
    this.saveLayout(this.filteredTiles.filter(tile => tile._id !== separator._id));
    this.layoutStatus = $localize`:@@tileSeparatorRemoved:Separator ${separator.name}:title: entfernt`;
  }

  public hideTile(tile: Tile): void {
    this.hiddenTileIds = new Set([...this.hiddenTileIds, tile._id]);
    this.applyLayout(this.filteredTiles, tile);
    this.layoutStatus = $localize`:@@tileLayoutHidden:${tile.name || tile.text}:name: von der Startseite entfernt`;
  }

  /** Holt eine entfernte Kachel zurück — sie landet wie bei iCloud am Ende. */
  public showTile(tile: Tile): void {
    this.hiddenTileIds = new Set([...this.hiddenTileIds].filter(id => id !== tile._id));
    const ordered = [...this.filteredTiles.filter(other => other._id !== tile._id), tile];
    this.applyLayout(ordered, tile);
    if (!this.hiddenTileIds.size) this.addTilesDialogVisible = false;
  }

  /** Eigene Startseite: zurück aufs Standardlayout des Mandanten (persönliche Kacheln bleiben unter
   * „nur für dich“). Standardlayout: löschen, danach gilt die eingebaute Reihenfolge. */
  public async resetLayout(): Promise<void> {
    if (this.editingDefaultLayout) {
      this.setLayout([]);
      this.layoutStatus = $localize`:@@tileDefaultLayoutResetDone:Standardlayout zurückgesetzt`;
      this.layoutSave = this.layoutSave.then(() => this.tileServide.resetDefaultTileLayout()).catch(() => this.reportLayoutError());
    } else {
      this.hasPersonalLayout = false;
      this.setLayout(this.resolvedDefaultLayout());
      this.layoutStatus = $localize`:@@tileLayoutResetDone:Standardanordnung wiederhergestellt`;
      this.layoutSave = this.layoutSave.then(() => this.tileServide.resetTileLayout()).catch(() => this.reportLayoutError());
    }
    await this.layoutSave;
  }

  private resolvedDefaultLayout(): TileLayoutItem[] {
    return resolveDefaultLayout(this.defaultLayoutItems, [...this.tiles, ...this.personalTiles], EXTRA_TILES_TITLE);
  }

  private setLayout(items: TileLayoutItem[]): void {
    this.tileLayout = items;
    this.tileSizes = Object.fromEntries(items.filter(item => !item.separator).map(item => [item.tileId, item.size]));
    this.hiddenTileIds = new Set(items.filter(item => item.hidden).map(item => item.tileId));
  }

  /** `ordered` sind die sichtbaren Kacheln in neuer Reihenfolge; ausgeblendete hängen hinten an. */
  private applyLayout(ordered: Tile[], changed: Tile): void {
    this.saveLayout(ordered);
    const visible = ordered.filter(tile => !this.hiddenTileIds.has(tile._id));
    const position = visible.indexOf(changed) + 1;
    this.layoutStatus = $localize`:@@tileLayoutMoved:${changed.name || changed.text}:name:: Position ${position}:position: von ${visible.length}:count:, ${this.tileSizeLabel(changed)}:size:`;
  }

  /** Speichert die neue Anordnung als eigenes Layout bzw. beim Bearbeiten als Standardlayout. */
  private saveLayout(ordered: Tile[]): void {
    const visible = ordered.filter(tile => !this.hiddenTileIds.has(tile._id));
    const hidden = this.allTiles.filter(tile => this.hiddenTileIds.has(tile._id));
    this.tileLayout = toTileLayout([...visible, ...hidden], this.tileSizes, this.hiddenTileIds);
    const items = this.tileLayout;
    const save = this.editingDefaultLayout
      ? () => this.tileServide.saveDefaultTileLayout(items)
      : () => this.tileServide.saveTileLayout(items);
    if (!this.editingDefaultLayout) this.hasPersonalLayout = true;
    this.layoutSave = this.layoutSave.then(save).catch(() => this.reportLayoutError());
  }

  private reportLayoutError(): void {
    this.toastrService.error($localize`:@@tileLayoutSaveError:Die Anordnung der Kacheln konnte nicht gespeichert werden.`);
  }

  private async loadTileLayout(): Promise<void> {
    try {
      const state = await this.tileServide.getTileLayout();
      this.hasPersonalLayout = state.personal;
      this.defaultLayoutItems = state.defaultItems;
      this.setLayout(state.personal ? state.items : this.resolvedDefaultLayout());
    } catch (error) {
      console.error('Failed to load tile layout', error);
      this.hasPersonalLayout = false;
      this.defaultLayoutItems = [];
      this.setLayout([]);
    }
  }

  /** Ohne Suche die Kacheln und Separatoren der Startseite (ohne entfernte), mit Suche alle
   * passenden Kacheln ohne Separatoren. */
  public get filteredTiles(): Tile[] {
    const terms = this.tileSearch.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean);
    if (!terms.length) {
      return this.hiddenTileIds.size ? this.allTiles.filter(tile => !this.hiddenTileIds.has(tile._id)) : this.allTiles;
    }
    return this.allTiles.filter(tile => {
      if (tile.separator) return false;
      const text = `${tile.name || tile.text || ''} ${tile.subtext || ''}`.toLocaleLowerCase();
      return terms.every(term => text.includes(term));
    });
  }

  public get totalTileCount(): number {
    return this.layoutTiles.length;
  }

  /** Zählt nur Kacheln, keine Separatoren. */
  public get filteredTileCount(): number {
    return this.filteredTiles.filter(tile => !tile.separator).length;
  }

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router, { optional: true });
  public readonly managementView = this.route.snapshot.data['manageTiles'] === true;
  public managedTiles: Tile[] = [];
  @ViewChild('webTileEditor') public webTileEditor?: WebTileEditorComponent;
  @ViewChild('personalTileEditor') private personalTileEditor?: WebTileEditorComponent;
  @ViewChild('widgetDialog') private widgetDialog?: HomeWidgetDialogComponent;

  public async onPersonalTileSaved(): Promise<void> {
    await this.loadPersonalTiles();
    // Ohne eigenes Layout landet eine neue persönliche Kachel unter „nur für dich“.
    if (!this.hasPersonalLayout && !this.editingDefaultLayout) this.setLayout(this.resolvedDefaultLayout());
    this.cdr.detectChanges();
  }

  private async loadPersonalTiles(): Promise<void> {
    try {
      this.personalTiles = await this.tileServide.listPersonalTiles();
    } catch (error) {
      console.error('Failed to load personal tiles', error);
      this.personalTiles = [];
    }
  }

  public async onWebTileSaved(): Promise<void> {
    this.tiles = await this.tileServide.listMyTiles();
    await this.loadActiveTilesEditor();
    this.cdr.detectChanges();
  }

  private refreshManagedTiles(): void {
    this.managedTiles = [...this.availableTiles, ...this.ownTiles];
  }

  public getSharingLabel(tile: Tile): string {
    if (this.tileVisibilityMode[tile._id] === 'selected') {
      const count = this.tileTargetUsers[tile._id]?.length ?? 0;
      return $localize`:@@companyAppsSelectedUsersCount:Ausgewählte Personen (${count}:count:)`;
    }
    return this.tileVisibilityOptions.find(option => option.value === this.tileVisibilityMode[tile._id])?.label ?? $localize`:@@portalManageMandantTilesModeOff:Deaktiviert`;
  }

  public getSharingSeverity(tile: Tile): 'success' | 'warn' | 'secondary' {
    if (this.tileVisibilityMode[tile._id] === 'all') return 'success';
    return this.tileVisibilityMode[tile._id] === 'selected' ? 'warn' : 'secondary';
  }
  /** Der Kachel-Pool, den der reflect-admin diesem Mandanten in der Mandantenverwaltung
   * zugewiesen hat — Auswahlmenge für den Dialog unten (nicht der gesamte globale Katalog). */
  public availableTiles: Tile[] = [];
  /** Eigene Weblink-Kacheln dieses Mandanten, ungefiltert (siehe TileService#listTenantTiles) —
   * bekommen dieselbe all/selected/off-Sichtbarkeitslogik wie Pool-Kacheln, nur pro Kachel statt
   * pro Mandant gespeichert (siehe TileService#setLinkVisibility). */
  public ownTiles: Tile[] = [];
  public mandantTilesDialogVisible = false;
  public selectedTileIds: string[] = [];
  public savingMandantTiles = false;
  public currentMandantName = '';
  /** Nutzer des aktuellen Mandanten inkl. ihrer individuellen Kachel-Grants (`tiles`) — Basis für
   * "Nur ausgewählte Personen" pro Kachel (siehe unten). */
  public mandantUsers: MandantUserWithTiles[] = [];
  /** Flache Anzeige-Liste für den Personen-Picker (ng-select mag keine verschachtelten Pfade als
   * bindLabel) — abgeleitet aus `mandantUsers`. */
  public mandantUserOptions: { id: string; name: string }[] = [];
  /** Pro Pool-Kachel: "all" = für alle Nutzer des Mandanten sichtbar (`activeTiles`), "selected" =
   * nur für die in `tileTargetUsers` gewählten Personen (individuelle Freigabe, `userTiles`),
   * "off" = für niemanden sichtbar (weder global noch individuell). Das ist die
   * Fallunterscheidung, die der Mandanten-Admin pro Kachel selbst trifft. */
  public tileVisibilityMode: Record<string, 'all' | 'selected' | 'off'> = {};
  /** Optionen für das p-select je Kachel-Zeile (siehe mandantTilesDialogVisible/tileSharingDialogVisible). */
  public tileVisibilityOptions: { label: string; value: 'all' | 'selected' | 'off' }[] = [
    { label: $localize`:@@portalManageMandantTilesModeOff:Deaktiviert`, value: 'off' },
    { label: $localize`:@@portalManageMandantTilesModeAll:Für alle sichtbar`, value: 'all' },
    { label: $localize`:@@portalManageMandantTilesModeSelected:Ausgewählte Personen`, value: 'selected' },
  ];
  /** Source-/Target-Listen für das p-picklist je Kachel (nur für Kacheln im Modus "selected"
   * relevant) — abgeleitet aus `mandantUserOptions` beim Laden, siehe loadActiveTilesEditor(). */
  public tileSourceUsers: Record<string, { id: string; name: string }[]> = {};
  public tileTargetUsers: Record<string, { id: string; name: string }[]> = {};

  /** Verstecktes Debug-Overlay (Strg+Alt+D) — zeigt zusätzlich Kacheln, die zur Organisation
   * gehören aber wegen fehlender Rollen-Berechtigung ausgeblendet sind (siehe
   * debugTileVisibility in tile-logto.service.ts). */
  public debugMode = false;
  public debugEntries: TileVisibilityDebugEntry[] = [];

  public get isReflactAdmin(): boolean {
    return !!this.userService.loginService.loggedInUser?.reflactadmin;
  }

  /** Mandanten-spezifischer Admin (Organization Role "admin" im aktuell ausgewählten Mandanten)
   * ODER reflectadmin — beide dürfen aus dem vom reflect-admin freigegebenen Pool (`tiles`)
   * auswählen, welche Kacheln für die Nutzer dieses Mandanten tatsächlich aktiv sind. */
  public get canManageActiveTiles(): boolean {
    return this.isReflactAdmin || !!this.userService.loginService.loggedInMandantUser?.permissions?.includes('admin');
  }

  @HostListener('window:keydown', ['$event'])
  public onKeydown(event: KeyboardEvent): void {
    if (event.ctrlKey && event.altKey && event.key.toLowerCase() === 'd') {
      this.toggleDebugMode();
    }
  }

  /** Fallback für den Fall, dass Strg+Alt+D vom Betriebssystem/Browser abgefangen wird — einfach
   * in der Browser-Konsole `toggleTileDebug()` aufrufen. */
  private toggleDebugMode = (): void => {
    this.debugMode = !this.debugMode;
    if (this.debugMode) {
      this.tileServide.listMyTilesDebug().then((entries) => {
        this.debugEntries = entries;
        this.cdr.detectChanges();
      });
    }
    this.cdr.detectChanges();
  };

  public getVisibilityReasonLabels(reasons: TileVisibilityReason[]): string[] {
    return reasons.map((reason) => VISIBILITY_REASON_LABELS[reason]);
  }

  public ngOnDestroy(): void {
    this.endTileDrag();
    this.cancelLongPress();
    delete (window as unknown as Record<string, unknown>)['toggleTileDebug'];
  }

  async ngOnInit() {
    (window as unknown as Record<string, unknown>)['toggleTileDebug'] = this.toggleDebugMode;
    this.tiles = await this.tileServide.listMyTiles();
    await this.loadPersonalTiles();
    if (!this.managementView) {
      await this.loadTileLayout();
    }
    const mandant = this.userService.loginService.selectedMandant$.value;
    this.currentMandantName = mandant?.name ?? '';
    if (this.managementView && this.canManageActiveTiles) {
      await this.loadActiveTilesEditor();
    }
    // Einstieg aus der Verwaltung („Standardlayout bearbeiten“): /home?standardlayout=1
    if (!this.managementView && this.route.snapshot.queryParamMap?.get('standardlayout') === '1') {
      await this.startEditingDefaultLayout();
    }
    // async ngOnInit continuations don't always trigger a new CD cycle on their own (same issue
    // as layout.component.ts) - force one so the tiles actually show up.
    this.cdr.detectChanges();
  }

  /** Lädt Pool (`tiles`), aktuell aktivierte Kacheln (`activeTiles`) und die Nutzer des Mandanten
   * für den "Kacheln verwalten"-Dialog — separat von `ngOnInit` und mit eigenem Error-Handling,
   * damit ein Fehler hier (z.B. Berechtigungsproblem) nicht stillschweigend zu einem leeren
   * Dropdown führt, sondern sichtbar gemeldet wird. */
  private async loadActiveTilesEditor(): Promise<void> {
    try {
      // `/api/mandant/mine` statt der reflectadmin-only Mandantenliste — liefert sowohl den
      // Pool (`tiles`) als auch die aktuell aktivierten Kacheln (`activeTiles`) des gerade
      // ausgewählten Mandanten, unabhängig davon ob der eingeloggte Nutzer reflectadmin ist.
      const [allTiles, myMandant, mandantUsers, ownTiles] = await Promise.all([
        this.tileServide.listTiles(),
        this.mandantService.getMyMandant(),
        this.userService.getMandantUsers(),
        this.tileServide.listTenantTiles(),
      ]);
      // mandant.service.ts erwartet historisch bedingt ObjectId-Typen für `tiles`, tatsächlich
      // sind Kachel-Ids seit dem Logto-Umstieg einfache Strings (siehe mandant.types.ts).
      const poolIds = (myMandant?.tiles ?? []) as unknown as string[];
      this.availableTiles = allTiles.filter((t) => poolIds.includes(t._id));
      this.ownTiles = ownTiles;
      this.mandantUsers = mandantUsers as MandantUserWithTiles[];
      this.mandantUserOptions = this.mandantUsers.map((u) => ({
        id: u._id.toString(),
        name: u.globalUser?.name || u.globalUser?.email || u._id.toString(),
      }));
      const activeTileIds = myMandant?.activeTiles ?? [];
      this.selectedTileIds = activeTileIds;
      this.tileVisibilityMode = {};
      this.tileSourceUsers = {};
      this.tileTargetUsers = {};
      for (const tile of this.availableTiles) {
        const assignedIds = this.mandantUsers.filter((u) => u.tiles?.includes(tile._id)).map((u) => u._id.toString());
        this.tileTargetUsers[tile._id] = this.mandantUserOptions.filter((o) => assignedIds.includes(o.id));
        this.tileSourceUsers[tile._id] = this.mandantUserOptions.filter((o) => !assignedIds.includes(o.id));
        if (activeTileIds.includes(tile._id)) {
          this.tileVisibilityMode[tile._id] = 'all';
        } else if (assignedIds.length > 0) {
          this.tileVisibilityMode[tile._id] = 'selected';
        } else {
          this.tileVisibilityMode[tile._id] = 'off';
        }
      }
      // Eigene Weblink-Kacheln: Sichtbarkeit steckt direkt in der Kachel (`linkVisibility`), nicht
      // im Mandanten-Dokument — sie gehören ohnehin nur zu genau diesem einen Mandanten.
      for (const tile of this.ownTiles) {
        const visibility = tile.linkVisibility ?? { mode: 'all' as const };
        const targetIds = visibility.mode === 'selected' ? (visibility.userIds ?? []) : [];
        this.tileTargetUsers[tile._id] = this.mandantUserOptions.filter((o) => targetIds.includes(o.id));
        this.tileSourceUsers[tile._id] = this.mandantUserOptions.filter((o) => !targetIds.includes(o.id));
        this.tileVisibilityMode[tile._id] = visibility.mode;
      }
      this.refreshManagedTiles();
    } catch (error) {
      console.error('Failed to load tile pool for the active-tiles editor', error);
      this.toastrService.error($localize`:@@portalManageMandantTilesLoadError:Der Kachel-Pool konnte nicht geladen werden. Bitte lade die Seite neu.`);
    }
  }
  public getTileUrl(tileUrl: string, kind?: Tile['kind']) {
    if (kind === 'link') return tileUrl;
    let returnUrl = tileUrl;
    returnUrl = returnUrl.replace("{portalLoginToken}", encodeURIComponent(localStorage.getItem("authToken") ?? ''));
    returnUrl = returnUrl.replace("{username}", encodeURIComponent(this.userService.loginService.loggedInUser?.name ?? ''));
    returnUrl = returnUrl.replace("{short}", encodeURIComponent(this.userService.loginService.selectedMandant$.value?.short ?? ''));

    // TODO: replace {portalLoginToken} and {username} here
    return returnUrl
  }

  public openMandantTilesDialog(): void {
    if (!this.managementView || !this.canManageActiveTiles) return;
    this.mandantTilesDialogVisible = true;
    // Frisch nachladen statt nur den ngOnInit-Snapshot zu verwenden — der reflect-admin könnte
    // den Pool geändert haben, seit diese Seite (ohne Reload) geöffnet wurde.
    this.loadActiveTilesEditor().then(() => this.cdr.detectChanges());
  }

  /** Steuert das schlanke Freigabe-Popup für genau eine Kachel (siehe "…"-Menü auf der
   * Kachel-Karte) — nutzt dieselben Daten/Methoden wie der "Kacheln verwalten"-Dialog, zeigt aber
   * nur die Zeile der angeklickten Kachel. */
  public tileSharingDialogVisible = false;
  public sharingTile?: Tile;

  public openTileSharingDialog(tile: Tile): void {
    if (!this.managementView || !this.canManageActiveTiles) return;
    this.sharingTile = tile;
    this.tileSharingDialogVisible = true;
    this.loadActiveTilesEditor().then(() => this.cdr.detectChanges());
  }

  /** Öffnet den Inhalts-Editor (Name/URL/Icon) einer eigenen Weblink-Kachel — getrennt von der
   * Freigabe (siehe openTileSharingDialog), da beides jetzt unabhängig voneinander steuerbar ist. */
  public editOwnWebTile(tile: Tile): void {
    if (!this.managementView || !this.canManageActiveTiles) return;
    this.webTileEditor?.open(tile);
  }

  /** Setzt pro Kachel die Fallunterscheidung, die der Mandanten-Admin trifft: für alle sichtbar
   * oder nur für die unten gewählten Personen (siehe saveMandantTiles). */
  public setTileVisibilityMode(tileId: string, mode: 'all' | 'selected' | 'off'): void {
    this.tileVisibilityMode[tileId] = mode;
  }


  public setTileTargetUsers(tileId: string, target: { id: string; name: string }[]): void {
    this.tileTargetUsers[tileId] = target;
  }

  /** Speichert pro Kachel entweder die Mandanten-weite Aktivierung (`activeTiles`) oder die
   * individuelle Freigabe an die gewählten Personen (`userTiles`) — je nachdem, was der
   * Mandanten-Admin für diese Kachel gewählt hat. Beides geht in EINEM Request an
   * `/api/mandant/setTileSharing` (#122), der Server führt die Freigaben mit dem aktuellen Stand
   * zusammen. Lädt danach die Portal-Ansicht direkt neu. */
  public async saveMandantTiles(): Promise<void> {
    if (this.savingMandantTiles) return;
    this.savingMandantTiles = true;
    let saved = false;
    try {
      const activeTileIds = this.availableTiles
        .filter((t) => this.tileVisibilityMode[t._id] === 'all')
        .map((t) => t._id);
      const selectedUsersByTile: Record<string, string[]> = {};
      for (const tile of this.availableTiles) {
        if (this.tileVisibilityMode[tile._id] === 'selected') {
          selectedUsersByTile[tile._id] = (this.tileTargetUsers[tile._id] ?? []).map((u) => u.id);
        }
      }
      const res = await this.mandantService.setTileSharing(activeTileIds, selectedUsersByTile);
      if (res.status !== 'ok') {
        this.toastrService.error($localize`:@@globalSaveFailedNoId:Das Speichern ist fehlgeschlagen. Bitte lade die Seite neu und versuche es erneut.`);
        return;
      }

      // Eigene Weblink-Kacheln: Sichtbarkeit steckt direkt in der Kachel, nicht im
      // Mandanten-Dokument — daher ein eigener Request pro Kachel statt eines Sammel-Updates.
      for (const tile of this.ownTiles) {
        const mode = this.tileVisibilityMode[tile._id] ?? 'all';
        const userIds = mode === 'selected' ? (this.tileTargetUsers[tile._id] ?? []).map((u) => u.id) : undefined;
        await this.tileServide.setLinkVisibility(tile._id, mode, userIds);
      }

      this.mandantTilesDialogVisible = false;
      this.tileSharingDialogVisible = false;
      saved = true;
      this.toastrService.success($localize`:@@globalSaved:Gespeichert!`);
      this.ownTiles = await this.tileServide.listTenantTiles();
      this.tiles = await this.tileServide.listMyTiles();
      this.refreshManagedTiles();
    } catch (error) {
      console.error(saved ? 'Failed to reload tiles after saving tile sharing' : 'Failed to save tile sharing', error);
      // Gespeichert ist dann schon — nur das Neuladen der Ansicht ist fehlgeschlagen.
      if (!saved) this.toastrService.error($localize`:@@globalSaveFailedNoId:Das Speichern ist fehlgeschlagen. Bitte lade die Seite neu und versuche es erneut.`);
    } finally {
      this.savingMandantTiles = false;
      this.cdr.detectChanges();
    }
  }

  /** Warnt beim Neuladen/Verlassen der Seite, solange Kachelfreigaben gespeichert werden (#122). */
  @HostListener('window:beforeunload', ['$event'])
  public warnWhileSavingTiles(event: BeforeUnloadEvent): void {
    if (!this.savingMandantTiles) return;
    event.preventDefault();
    event.returnValue = ''; // Safari und ältere Browser zeigen die Warnung nur damit
  }

}
